<template>
  <v-container fluid v-if="show">
    <v-dialog v-model="show" max-width="460">
      <v-form name="form" ref="formRef" @submit.prevent="onSaveClick">

        <v-stepper v-model="e1" non-linear>
          <v-stepper-header>
            <v-stepper-step color="secondary" :complete="e1 > 1" step="1" :editable="checkPassword()"></v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="secondary" :complete="e1 > 2" step="2" :editable="checkPassword()"></v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="secondary" :complete="e1 > 3" step="3" :editable="checkPassword()"></v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="secondary" :complete="e1 > 4" step="4" :editable="checkPassword()"></v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="secondary" :complete="e1 > 5" step="5" :editable="checkPassword()"></v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="secondary" :complete="e1 > 6" step="6" :editable="checkPassword()"></v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="secondary" :complete="e1 > 7" step="7" :editable="checkPassword()"></v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!--Password-->
            <v-stepper-content step="1" :editable="checkPassword()">
              <div class="mb-8">
                <span class="header-font" style="font-size: 22px;"> {{ $t('login.password') }}</span>
                <p class="body-1">{{$t("login.firstLoginMessage")}}</p>
                <p class="body-1 font-weight-bold primary--text pb-1 mb-0">{{$t("login.password")}}</p>
                <v-text-field dense outlined
                  class="pa-0" 
                  @change="onInputChange()"  
                  v-model="user.password" 
                  ref="passwordRef"
                  :rules="passwordRules" 
                  type="password">
                </v-text-field>
                <p class="body-1 font-weight-bold primary--text pb-1 mb-0">{{$t("login.passwordConfirmation")}}</p>
                <v-text-field dense outlined
                  class="pa-0"
                  @change="onInputChange()"  
                  v-model="user.passwordConfirmation" 
                  ref="passwordConfirmationRef"
                  :rules="passwordConfirmationRules" 
                  type="password">
                </v-text-field>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onCancelClick()">{{$t('general.cancel')}}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="e1 = 2" :disabled="!checkPassword()">{{ $t('tours.buttons.next') }}</v-btn>
            </v-stepper-content>

            <!--Language-->
            <v-stepper-content step="2" :editable="checkPassword()">
              <div class="mb-12">
                <p class="body-1 font-weight-bold primary--text pb-1 mb-0">{{$t("settings.language")}}</p>
                <v-autocomplete dense outlined
                  v-model="user.languageId"
                  :items="languages"
                  item-text="name"
                  item-value="id"
                  menu-props="auto"
                  hide-details
                  :rules="languageRules"
                  v-on:input="changeLocale(user.languageId);"
                ></v-autocomplete>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onSaveClick()">{{ $t("login.skipSettings") }}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="e1 = 3">{{ $t('tours.buttons.next') }}</v-btn>
            </v-stepper-content>

            <!--Player Visualization-->
            <v-stepper-content step="3" :editable="checkPassword()">
              <div class="mb-8">
                <span class="pa-0 my-2 header-font" style="font-size: 22px;">{{ $t('settings.defaultPlayerVisualization') }}</span>
                <v-radio-group dense hide-details row class="mt-2 d-flex justify-space-between default-player-visualization-radio"
                  v-model="defaultVisualizationModel"
                  @change="onDefaultVisualizationChange($event)">
                  <v-radio color="secondary" :label="$t('playerRecruitment.radar')" value="radar" class="mt-1">
                    <template v-slot:label>
                      <span class="body-1 primary--text">{{$t('playerRecruitment.radar')}}
                        <sup><v-chip color="orange" text-color="white" class="p-0" pill x-small>
                          {{$t('settings.recommended')}}
                        </v-chip></sup>
                      </span>
                    </template>
                  </v-radio>
                  <v-radio color="secondary" :label="$t('playerRecruitment.lollipop')" value="lollipop" class="mt-1">
                    <template v-slot:label>
                      <span class="body-1 primary--text">{{ $t('playerRecruitment.lollipop') }}</span>
                    </template>
                  </v-radio>
                  <v-radio color="secondary" :label="$t('playerRecruitment.polar')" value="polar" class="mt-1">
                    <template v-slot:label>
                      <span class="body-1 primary--text">{{ $t('playerRecruitment.polar') }}</span>
                    </template>
                  </v-radio>
                </v-radio-group>
                <div class="d-flex flex-row justify-space-around mt-5">
                  <v-img :src="require(`@/assets/img/visualization_${defaultVisualizationModel}.png`)" height="200" contain></v-img>
                </div>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onSaveClick()">{{ $t("login.skipSettings") }}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="e1 = 4">{{ $t('tours.buttons.next') }}</v-btn>
            </v-stepper-content>
            
            <!--Field color-->
            <v-stepper-content step="4" :editable="checkPassword()">
              <div class="mb-12">
                <span class="pa-0 my-2 header-font" style="font-size: 22px;">{{ $t('settings.fieldColor') }}</span>
                <v-radio-group 
                  dense 
                  hide-details 
                  row 
                  class="mt-2 d-flex justify-space-between settings-language-radio"
                  v-model="fieldColorModel"
                  @change="onFieldColorChange($event)">
                    <v-radio v-for="(fieldColor) of fieldColors"  color="secondary"
                      :label="fieldColor" 
                      :value="fieldColor"
                      :key="fieldColor" 
                      class="mt-1">
                        <template v-slot:label>
                          <span class="body-1 primary--text">{{$t(`settings.fieldColors.${fieldColor}`)}}</span>
                        </template>
                    </v-radio>
                  </v-radio-group>
                  <div class="d-flex flex-row justify-space-around mt-5">
                    <v-img :src="require(`@/assets/img/half_pitch_${fieldColorModel}.png`)" max-width="160" class="me-2"></v-img>
                    <v-img :src="require(`@/assets/img/pitch_${fieldColorModel}.png`)" max-width="160" class="ms-2"></v-img>
                  </div>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onSaveClick()">{{ $t("login.skipSettings") }}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="e1 = 5">{{ $t('tours.buttons.next') }}</v-btn>
            </v-stepper-content>

            <!--Field Positions-->
            <v-stepper-content step="5" :editable="checkPassword()">
              <div class="mb-12">
                <span class="pa-0 my-2 header-font" style="font-size: 22px;">{{ $t('faq.categories.playerPositions') }}</span>
                <v-radio-group 
                  dense 
                  hide-details 
                  row 
                  class="mt-2 d-flex justify-space-between settings-language-radio"
                  v-model="fieldPositionModel"
                  @change="onFieldPositionChange($event)"
                >
                  <v-radio v-for="(fieldPositionOption) of fieldPositionOptions" color="secondary"
                    :label="fieldPositionOption.code" 
                    :value="fieldPositionOption.id"
                    :key="fieldPositionOption.id" 
                    class="mt-1">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{$t(`settings.fieldPositions.${fieldPositionOption.code}`)}}</span>
                      </template>
                  </v-radio>
                </v-radio-group>
                <v-row class="alig-center justify-center mt-4">
                  <div class="d-flex align-center w-100 h-80" ref="pitch">
                    <v-img class="rounded" :src="require(`@/assets/img/pitch_${fieldColorModel}.png`)">
                      <div class="position_field d-flex flex-wrap h-100 justify-space-between align-center">
                        <div v-for="(position,index) in fieldPositions" :key="index" class="w-16 h-32 d-flex justify-center"
                          v-bind:class="{'caja':position}"
                          v-bind:style="{'background-color': `rgba(0,0,255,${position.length === 0 ? 0 : 0.195}`}"
                        >
                          <div class="d-flex justify-center align-center w-100 " v-if="position">
                              <v-chip class="monospace-font" style="height: 25px" :color="getPositionChip(position)" text-color="white" small>{{ getPositionCode(position) }}</v-chip>
                          </div>
                        </div>
                      </div>
                    </v-img>
                  </div>
                </v-row>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onSaveClick()">{{ $t("login.skipSettings") }}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="e1 = 6">{{ $t('tours.buttons.next') }}</v-btn>
            </v-stepper-content>

            <!--Currency-->
            <v-stepper-content step="6" :editable="checkPassword()">
              <div class="mb-12">
                <p class="body-1 font-weight-bold primary--text pb-1 mb-0">{{$t("settings.currency")}}</p>
                <v-autocomplete dense outlined
                  v-model="user.currency"
                  :items="currencies"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  hide-details
                  :rules="currencyRules"
                  v-on:input="changeCurrency(user.currency);"
                >
                  <template v-slot:selection="{ item }">
                    <DriblabCountryFlag :iso="item.countryCode" ></DriblabCountryFlag>
                    <span class="ml-1">{{$t(item.name)}}</span>
                  </template>
                  <template slot="item" slot-scope="data">
                    <DriblabCountryFlag :iso="data.item.countryCode" ></DriblabCountryFlag>
                    <span class="ml-1">{{$t(data.item.name)}}</span>
                  </template>
                </v-autocomplete>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onSaveClick()">{{ $t("login.skipSettings") }}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="e1 = 7">{{ $t('tours.buttons.next') }}</v-btn>
            </v-stepper-content>

            <!--Dark mode-->
            <v-stepper-content step="7" :editable="checkPassword()">
              <div class="mb-8 d-flex justify-center">
                <span class="header-font me-5" style="font-size: 22px;">{{$t("settings.darkMode")}}</span>
                <v-switch inset label hide-details class="d-flex align-center ma-0 pa-0" v-model="darkMode" dense @change="onDarkModeChange()" color="secondary">
                  <template v-slot:prepend>
                    <v-icon small>wb_sunny</v-icon>
                  </template>
                  <template v-slot:label>
                    <v-icon class="mr-1" small>nights_stay</v-icon>
                  </template>
                </v-switch>
              </div>
              <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4 me-2" @click="onCancelClick()">{{$t('general.cancel')}}</v-btn>
              <v-btn rounded class="elevation-0 monospace-font white--text px-4 me-2" color="primary" @click="onSaveClick()">{{$t('general.save')}}</v-btn>
            </v-stepper-content>

          </v-stepper-items>
        </v-stepper>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<script>

import Dictionary from '../../dictionary';
import * as _ from 'lodash';
import DriblabCountryFlag from "./DriblabCountryFlag";
import { getCurrencies, getChipColorByPosition, getPositionCode } from '../../utils/functions';
import UserService from "../../services/user.service";

export default {
  name: 'NewPassFormModal',
  components: {
    DriblabCountryFlag
  },
  filters: {
  },
  computed: {
    currencies(){
      return getCurrencies();
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    defaultBrowserLanguage() {
      const browserLanguage = navigator.language ||  navigator.userLanguage;
      const code = browserLanguage.split('-')[0];
      const defaultLanguage = _.find(this.languages, (l) => l.code === code);
      //If language is found, return its code. If not, return code for english language
      return defaultLanguage ? defaultLanguage.code : 'en'; 
    },
  },
  props:{
    title:{
      type:String,
      default: "login.firstLogin"
    }
  },
  data() {
    return {
      user: null,
      show: false,
      e1: 1,
      passwordRules: [
        v => !!v || Dictionary[this.$i18n.locale]["general"]["formErrors"]["required"],
        v => _.size(v) >= 8 || Dictionary[this.$i18n.locale]["general"]["formErrors"]["minLength"] + " (8)",
      ],
      passwordConfirmationRules: [
        v => !!v || Dictionary[this.$i18n.locale]["general"]["formErrors"]["required"],
        v => v === this.user.password || Dictionary[this.$i18n.locale]["general"]["formErrors"]["passwordMatch"],
        v => _.size(v) >= 8 || Dictionary[this.$i18n.locale]["general"]["formErrors"]["minLength"] + " (8)",
      ],
      languageRules: [
        v => !!v || Dictionary[this.$i18n.locale]["general"]["formErrors"]["required"]
      ],
      languages: [],
      currencyRules: [
        v => !!v || Dictionary[this.$i18n.locale]["general"]["formErrors"]["required"]
      ],
      fieldColors: ['green', 'blue', 'white', 'black'],
      fieldColorModel: 'green',
      defaultVisualizationModel: 'radar',
      leagues: {},
      leagueAreas: {
        0: "Europe",
        1: "Europe",
        2: "Europe",
        3: "South America",
        4: "South America",
        5: "North America",
        6: "Asia",
        7: "Africa",
        8: "Youth",
        9: "Youth Int",
        10: "National Team",
        11: "Other Competitions",
        12: "Women",
        99: "Unknown"
      },
      darkMode: false,
      fieldPositions:[ "", 4, 16, 8, 11, 13, 1, 2, 5, 6, 9, 14, "", 3, 15, 7, 10, 12 ],
      fieldPositionOptions: [{ id: 1, code: 'default' }, { id: 2, code: 'fifa' }, { id: 3, code: 'spanish' }, { id: 4, code: 'numeric' }],
      fieldPositionModel: 1
    };
  },
  created() {
    this.setLanguages();
  },
  methods: {
    open(user = {}){
      this.user = user;
      this.show = true;
      //Selected values by default
      this.user.currency = "EUR";
      this.user.languageId = this.getIdFromLanguageCode(this.defaultBrowserLanguage);
      this.changeLocale(this.user.languageId);
    },
    close(){
      this.user = null;
      this.show = false;
    },
    onCancelClick(){
      this.$emit('save',null),
      this.close();
    },
    async onSaveClick(){
      if (this.$refs.formRef.validate()) {
        //Set default values if not selected
        this.changeLocale(this.user.languageId);
        this.changeCurrency(this.user.currency);
        this.onFieldColorChange(this.fieldColorModel);
        this.onDefaultVisualizationChange(this.defaultVisualizationModel);
        this.onDarkModeChange(this.darkMode);
        this.$emit('save',this.user);
        this.close();
      }
    },
    onInputChange(){
      this.$refs.formRef.validate();
    },
    changeLocale(langId) {
      let obj = _.find(this.languages,(l) => l.id === langId);
      let locale = _.get(obj,'code');
      this.$store.dispatch("auth/setLanguage", locale);
      UserService.postUserLanguage(this.$store.state.auth.user.id, this.getIdFromLanguageCode(langId));
      this.setLanguages();
    },
    setLanguages() {
      this.languages = [
        { code: "en", name: this.$t("langs.en"), id: 37 },
        { code: "es", name: this.$t("langs.es"), id: 39 },
        { code: "fr", name: this.$t("langs.fr"), id: 47 },
        { code: "it", name: this.$t("langs.it"), id: 72 },
        { code: "de", name: this.$t("langs.de"), id: 32 },
        { code: "pt", name: this.$t("langs.pt"), id: 130 },
        { code: "tr", name: this.$t("langs.tr"), id: 9 },
        { code: "jp", name: this.$t("langs.jp"), id: 803 },
        { code: "cn", name: this.$t("langs.cn"), id: 14 }
      ]
    },
    changeCurrency(currencyCode) {
      this.$store.dispatch("auth/setCurrency", currencyCode);
      UserService.putUserCurrency(currencyCode);
    },
    onFieldColorChange(fieldColor){
      this.$store.dispatch("auth/setFieldColor", fieldColor);
      UserService.putUserFieldColor(fieldColor);
    },
    checkPassword() {
      if (this.$refs.passwordRef && this.$refs.passwordConfirmationRef) {
        return this.$refs.passwordRef.validate() && this.$refs.passwordConfirmationRef.validate();
      };
    },
    onDefaultVisualizationChange(defaultVisualization){
      this.$store.dispatch("auth/setDefaultVisualization", defaultVisualization);
      UserService.putUserDefaultVisualization(defaultVisualization);
    },  
    getIdFromLanguageCode(code) {
      const languageObj = this.languages.find(lang => lang.code === code);
      return languageObj ? languageObj.id : 37;
    },
    onDarkModeChange() {
      this.$vuetify.theme.dark = this.darkMode;
      UserService.switchTheme(this.currentUser.id, this.darkMode ? "dark" : "light");
      this.$store.dispatch("auth/setDarkmode", this.darkMode);
    },
    getPositionChip(position) {
      return getChipColorByPosition(position);
    },
    getPositionCode(position) {
      return getPositionCode(this.currentUser.fieldPositionCodeId, position);
    },
    onFieldPositionChange(fieldPositionCodeId) {
      this.$store.dispatch("auth/setFieldPositionCodeId", fieldPositionCodeId);
      UserService.putUserFieldPositionCode(fieldPositionCodeId);
    }
  },
};
</script>

<style scoped lang="scss">
.caja{
  border: 1px solid white;
}
.position_field{
  padding: 6% 8% 6% 9%;
}

html.is-rtl * {
    direction: rtl;
}
</style>



// WEBPACK FOOTER //
// src/views/base/NewPassFormModal.vue